import React, {useEffect, useMemo, useRef, useState} from 'react';
import AppointmentCard from './AppointmentCard';
import {
    REST_HATCH, getDayHours, isDayClosed, isFullDayRest, restDaysForDay, restHoursLabel, restKindLabel
} from './restDays';
import {dayKey, getWeekDays, isSameDay, layoutOverlappingEvents} from './calendarRange';

// Week view (ported from the POS tablets' WeekView): 7 day columns (Mon → Sun)
// × hour rows. Time is the Y axis here, unlike the Day view where it is the X
// axis. Every visible professional's appointments share the day column;
// overlapping ones sit side by side, each card tagged with the pro's colour.

const HOUR_HEIGHT = 60;   // px per hour row
const HOUR_COL_WIDTH = 56;
const MIN_DAY_WIDTH = 120;

const minutesFrom = (date, startHour) => (date.getHours() - startHour) * 60 + date.getMinutes();

const WeekView = ({
    currentDate,
    events,
    resources,          // visible (filtered) professionals
    hours,              // e.g. [9, 10, …, 22]
    onSlotClick,        // (date, {hour, minute}) → open "new appointment"
    onEventClick,
    getEventActions,    // (event) → ActionMenu items of the card's "⋯" menu
    onDayClick,         // header click → open that day in the Day view
    restDays = [],      // employees' Repos / Congé (hatched, not bookable)
    openingHours = null,// shop hours per weekday: closed days + hours outside opening are hatched
    onRestClick,        // (rest) → edit it
}) => {
    const days = useMemo(() => getWeekDays(currentDate), [currentDate]);
    const startHour = hours[0];
    const endHour = hours[hours.length - 1] + 1;
    const gridHeight = hours.length * HOUR_HEIGHT;
    const scrollRef = useRef(null);

    // Re-render every minute so the "now" line moves
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(id);
    }, []);

    const resourceById = useMemo(() => {
        const map = {};
        resources.forEach(r => { map[r.id] = r; });
        return map;
    }, [resources]);

    const eventsByDay = useMemo(() => {
        const map = {};
        days.forEach(d => { map[dayKey(d)] = []; });
        events.forEach(e => {
            if (!(e.start instanceof Date) || !resourceById[e.resourceId]) return;
            const key = dayKey(e.start);
            if (map[key]) map[key].push(e);
        });
        Object.keys(map).forEach(k => { map[k] = layoutOverlappingEvents(map[k]); });
        return map;
    }, [days, events, resourceById]);

    const restsByDay = useMemo(() => {
        const map = {};
        days.forEach(d => {
            map[dayKey(d)] = restDaysForDay(restDays, d).filter(r => resourceById[r.employee_id]);
        });
        return map;
    }, [days, restDays, resourceById]);
    // One pro shown → rest blocks at their hours; several → tags in the day header
    const singlePro = resources.length === 1;

    // Open on the current hour when this week contains today
    useEffect(() => {
        if (!scrollRef.current) return;
        const today = days.some(d => isSameDay(d, new Date()));
        const h = new Date().getHours();
        scrollRef.current.scrollTop = today && h > startHour ? (h - startHour - 1) * HOUR_HEIGHT : 0;
    }, [days, startHour]);

    const nowMinutes = minutesFrom(now, startHour);
    const showNowLine = nowMinutes >= 0 && nowMinutes <= (endHour - startHour) * 60;

    return (
        <div ref={scrollRef} className="flex-1 overflow-auto bg-white dark:bg-gray-900">
            <div style={{minWidth: HOUR_COL_WIDTH + 7 * MIN_DAY_WIDTH}}>

                {/* ── Day headers (sticky) ── */}
                <div className="flex sticky top-0 z-30 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex-shrink-0 sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
                         style={{width: HOUR_COL_WIDTH}}/>
                    {days.map(day => {
                        const isToday = isSameDay(day, now);
                        const count = eventsByDay[dayKey(day)]?.length || 0;
                        return (
                            <button key={dayKey(day)} type="button" onClick={() => onDayClick(day)}
                                    title="Voir la journée"
                                    className="flex-1 flex flex-col items-center py-2 border-r border-gray-200 dark:border-gray-700 hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                                    style={{minWidth: MIN_DAY_WIDTH}}>
                                <span className={`text-xs font-semibold uppercase tracking-wider ${isToday ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`}>
                                    {day.toLocaleDateString('fr-FR', {weekday: 'short'}).replace('.', '')}
                                </span>
                                <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-base font-bold tabular-nums ${
                                    isToday ? 'bg-primary text-white' : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                    {day.getDate()}
                                </span>
                                <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                    {isDayClosed(openingHours, day)
                                        ? <span className="font-semibold text-red-500">Fermé</span>
                                        : count === 0 ? '—' : `${count} RDV${count > 1 ? 's' : ''}`}
                                </span>
                                {!singlePro && (restsByDay[dayKey(day)] || []).length > 0 && (
                                    <span className="mt-1 flex flex-wrap justify-center gap-1 px-1">
                                        {restsByDay[dayKey(day)].slice(0, 3).map(rest => {
                                            const pro = resourceById[rest.employee_id];
                                            return (
                                                <span key={rest.id} role="button" tabIndex={0}
                                                      onClick={(e) => { e.stopPropagation(); onRestClick?.(rest); }}
                                                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onRestClick?.(rest); } }}
                                                      title={`${restKindLabel(rest)} · ${pro.name} · ${restHoursLabel(rest)}`}
                                                      className="inline-flex items-center gap-1 max-w-full px-1.5 py-px rounded-full text-[10px] font-semibold bg-gray-200/80 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{backgroundColor: pro.color}}/>
                                                    <span className="truncate">{restKindLabel(rest)} {pro.name.split(' ')[0]}</span>
                                                </span>
                                            );
                                        })}
                                        {restsByDay[dayKey(day)].length > 3 && (
                                            <span className="text-[10px] font-semibold text-gray-500">+{restsByDay[dayKey(day)].length - 3}</span>
                                        )}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* ── Agenda grid ── */}
                <div className="flex">
                    {/* Hour labels */}
                    <div className="flex-shrink-0 sticky left-0 z-20 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700"
                         style={{width: HOUR_COL_WIDTH}}>
                        {hours.map(h => (
                            <div key={h} className="relative text-right pr-2" style={{height: HOUR_HEIGHT}}>
                                <span className="relative -top-2 text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums bg-white dark:bg-gray-900">
                                    {h === startHour ? '' : `${h}:00`}
                                </span>
                            </div>
                        ))}
                    </div>

                    {days.map(day => {
                        const isToday = isSameDay(day, now);
                        const dayEvents = eventsByDay[dayKey(day)] || [];
                        return (
                            <div key={dayKey(day)}
                                 className={`relative flex-1 border-r border-gray-200 dark:border-gray-700 ${isToday ? 'bg-primary/[0.04] dark:bg-primary/[0.06]' : ''}`}
                                 style={{minWidth: MIN_DAY_WIDTH, height: gridHeight}}>

                                {/* Empty hour cells — click a half-hour to create an appointment */}
                                {hours.map(h => (
                                    <div key={h} className="border-b border-gray-100 dark:border-gray-800" style={{height: HOUR_HEIGHT}}>
                                        {[0, 30].map(minute => (
                                            <div key={minute}
                                                 onClick={() => onSlotClick(day, {hour: h, minute})}
                                                 className={`h-1/2 cursor-pointer hover:bg-primary/10 dark:hover:bg-primary/15 transition-colors ${
                                                     minute === 0 ? 'border-b border-dashed border-gray-100 dark:border-gray-800' : ''
                                                 }`}/>
                                        ))}
                                    </div>
                                ))}

                                {/* Closed day, or before / after opening hours */}
                                {isDayClosed(openingHours, day) ? (
                                    <div className="absolute inset-0 z-[1] pointer-events-none bg-gray-100/60 dark:bg-gray-800/60" style={REST_HATCH}/>
                                ) : (() => {
                                    const open = getDayHours(openingHours, day);
                                    if (!open) return null;
                                    return [[startHour, open.start], [open.end, endHour]]
                                        .filter(([a, b]) => b > a)
                                        .map(([a, b]) => (
                                            <div key={a} className="absolute left-0 right-0 z-[1] pointer-events-none bg-gray-100/50 dark:bg-gray-800/50"
                                                 style={{...REST_HATCH, top: (Math.max(a, startHour) - startHour) * HOUR_HEIGHT, height: (Math.min(b, endHour) - Math.max(a, startHour)) * HOUR_HEIGHT}}/>
                                        ));
                                })()}

                                {/* Repos / congé of the (single) pro shown */}
                                {singlePro && (restsByDay[dayKey(day)] || []).map(rest => {
                                    const from = Math.max(Number(rest.start_time), startHour);
                                    const to = Math.min(Number(rest.end_time), endHour);
                                    if (to <= from) return null;
                                    return (
                                        <button key={rest.id} type="button" onClick={() => onRestClick?.(rest)}
                                                title={`${restKindLabel(rest)} · ${restHoursLabel(rest)} — cliquer pour modifier`}
                                                className="absolute left-0 right-0 z-[2] flex flex-col items-start px-2 py-1 text-left bg-gray-100/80 dark:bg-gray-800/80 border-y border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-200/70 dark:hover:bg-gray-700/70 transition-colors"
                                                style={{...REST_HATCH, top: (from - startHour) * HOUR_HEIGHT, height: (to - from) * HOUR_HEIGHT}}>
                                            <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300">{restKindLabel(rest)}</span>
                                            {!isFullDayRest(rest) && (
                                                <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">{restHoursLabel(rest)}</span>
                                            )}
                                        </button>
                                    );
                                })}

                                {/* "Now" line — today's column only */}
                                {isToday && showNowLine && (
                                    <div className="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
                                         style={{top: nowMinutes * HOUR_HEIGHT / 60}}>
                                        <span className="w-2.5 h-2.5 -ml-1.5 rounded-full bg-red-500"/>
                                        <span className="flex-1 h-0.5 bg-red-500"/>
                                    </div>
                                )}

                                {/* Event cards */}
                                {dayEvents.map(({event, col, cols}) => {
                                    const resource = resourceById[event.resourceId];
                                    const startMin = Math.max(minutesFrom(event.start, startHour), 0);
                                    const endMin = Math.min(minutesFrom(event.end, startHour), (endHour - startHour) * 60);
                                    const top = startMin * HOUR_HEIGHT / 60;
                                    const height = Math.max((endMin - startMin) * HOUR_HEIGHT / 60 - 2, 22);
                                    const widthPct = 100 / cols;
                                    return (
                                        <AppointmentCard key={event.id}
                                                         event={event}
                                                         resource={resource}
                                                         height={height}
                                                         showResource={resources.length > 1}
                                                         onClick={onEventClick}
                                                         getActions={getEventActions}
                                                         style={{
                                                             top,
                                                             left: `calc(${col * widthPct}% + 2px)`,
                                                             width: `calc(${widthPct}% - 4px)`,
                                                         }}/>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default WeekView;
